/**
 * The outcome capabilities the platform is deciding on, as a flat list of chips.
 *
 * Situations and segments are no longer a browsable category, so only the outcome
 * capabilities appear here. The questions the other trees hold surface in the
 * knowledge card instead.
 */
import { Outcome } from "@financial-advisory/data-access/api-types";
import { UnlikelyBadge } from "@targeted-support/ui/features/overview";
import { Target } from "lucide-react";
import type { SystemState } from "@unlikelyai-magic/utils";
import { CollapsibleCard } from "./CollapsibleCard";
import { OutcomeChip, outcomeChipState } from "./OutcomeChip";

interface OutcomeCapabilitiesCardProps {
  systemState: SystemState;
  isExpanded: boolean;
  onToggle: () => void;
  onSelectOutcome?: (productTreeId: string) => void;
}

export const OutcomeCapabilitiesCard = ({
  systemState,
  isExpanded,
  onToggle,
  onSelectOutcome,
}: OutcomeCapabilitiesCardProps) => {
  const outcomes = Array.from(systemState.suggestions.values());
  const decided = outcomes.filter(
    (outcome) =>
      outcome.value === Outcome.TRUE || outcome.value === Outcome.FALSE
  ).length;

  return (
    <CollapsibleCard
      icon={Target}
      title="Outcome capabilities"
      badge={<UnlikelyBadge />}
      isExpanded={isExpanded}
      onToggle={onToggle}
      bodyClassName="max-h-[168px]"
      meta={
        <span className="text-xs text-brand-muted">
          {decided} of {outcomes.length} decided
        </span>
      }
    >
      <div className="flex flex-wrap gap-2">
        {outcomes.map((outcome) => (
          <OutcomeChip
            key={outcome.productTreeId}
            name={outcome.name}
            state={outcomeChipState(outcome.value)}
            onClick={
              onSelectOutcome
                ? () => onSelectOutcome(outcome.productTreeId)
                : undefined
            }
          />
        ))}
      </div>
    </CollapsibleCard>
  );
};
